import {
  cabinetProfiles,
  cabinetSystems,
  canUseOnChannel,
  isVersionAtLeast,
  softwareStylePageKeys
} from '@bf/domain'
import type {
  ContentEntity,
  ContentManifest,
  DeliveryChannel,
  MediaAsset
} from '@bf/domain'
import { isForbiddenMediaSha256 } from './media-policy'
import { contentManifestSchema } from './schema'

const V010_MIN_APP_VERSION = '0.1.0'
const V020_MIN_APP_VERSION = '0.2.0'
const V010_ALLOWED_SPECIALIST_DOMAINS = new Set(['ba', 'ddc'])
const CABINET_LAYOUT_TEMPLATE_IDS = new Set(
  cabinetProfiles.flatMap((cabinet) =>
    cabinetSystems.map((system) => `${cabinet.id}-${system.id}`)
  )
)

export interface ContentValidationIssue {
  path: string
  message: string
}

export interface ContentValidationResult {
  success: boolean
  data?: ContentManifest
  issues: ContentValidationIssue[]
}

function findDuplicates(values: string[]): string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value)
    seen.add(value)
  }
  return [...duplicates]
}

export function validateContent(input: unknown): ContentValidationResult {
  const parsed = contentManifestSchema.safeParse(input)
  if (!parsed.success) {
    return {
      success: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message
      }))
    }
  }

  const data = parsed.data as ContentManifest
  const issues: ContentValidationIssue[] = []
  const entityIds = data.entities.map((entity) => entity.id)
  const assetIds = data.assets.map((asset) => asset.id)
  const assetPaths = data.assets.map((asset) =>
    asset.path.normalize('NFC').toLowerCase()
  )
  const allIds = [...entityIds, ...assetIds]

  for (const duplicate of findDuplicates(allIds)) {
    issues.push({ path: duplicate, message: 'ID 重复' })
  }
  for (const duplicate of findDuplicates(assetPaths)) {
    issues.push({
      path: `assets.${duplicate}`,
      message: '媒体路径重复'
    })
  }

  for (const asset of data.assets) {
    if (isForbiddenMediaSha256(asset.sha256)) {
      issues.push({
        path: `assets.${asset.id}.sha256`,
        message: '媒体 SHA-256 命中禁用素材清单'
      })
    }
    if (
      asset.displayRole === 'cabinet-layout' &&
      asset.layoutTemplateId === null
    ) {
      issues.push({
        path: `assets.${asset.id}.layoutTemplateId`,
        message: '机柜布局素材必须绑定布局模板 ID'
      })
    }
    if (
      asset.layoutTemplateId !== null &&
      !CABINET_LAYOUT_TEMPLATE_IDS.has(asset.layoutTemplateId)
    ) {
      issues.push({
        path: `assets.${asset.id}.layoutTemplateId`,
        message: '素材绑定了未知的机柜布局模板'
      })
    }
    if (
      asset.layoutTemplateId !== null &&
      asset.displayRole !== 'cabinet-layout' &&
      asset.displayRole !== 'thumbnail'
    ) {
      issues.push({
        path: `assets.${asset.id}.displayRole`,
        message: '只有机柜布局或其缩略图可以绑定布局模板 ID'
      })
    }
  }

  const preferredLayoutIds = data.assets
    .filter(
      (asset) =>
        asset.displayRole === 'cabinet-layout' &&
        asset.preferred &&
        asset.layoutTemplateId !== null
    )
    .map((asset) => asset.layoutTemplateId as string)
  for (const duplicate of findDuplicates(preferredLayoutIds)) {
    issues.push({
      path: `assets.layoutTemplateId.${duplicate}`,
      message: '同一机柜布局只能有一个首选素材'
    })
  }

  const assetIdSet = new Set(assetIds)
  const entityIdSet = new Set(entityIds)

  for (const entity of data.entities) {
    if (entity.contentVersion !== data.contentVersion) {
      issues.push({
        path: `entities.${entity.id}.contentVersion`,
        message: '实体内容版本与清单不一致'
      })
    }
    if (
      entity.contentType !== 'company' &&
      entity.systemDomains.some(
        (domain) => !V010_ALLOWED_SPECIALIST_DOMAINS.has(domain)
      )
    ) {
      issues.push({
        path: `entities.${entity.id}.systemDomains`,
        message: 'DDC/BA 专版的非公司内容只能使用 ba 或 ddc 领域'
      })
    }
    for (const mediaId of entity.mediaIds) {
      if (!assetIdSet.has(mediaId)) {
        issues.push({
          path: `entities.${entity.id}.mediaIds`,
          message: `引用了不存在的媒体 ${mediaId}`
        })
      }
    }
    if (entity.contentType === 'project-case') {
      for (const relatedId of entity.relatedEntityIds) {
        if (!entityIdSet.has(relatedId)) {
          issues.push({
            path: `entities.${entity.id}.relatedEntityIds`,
            message: `引用了不存在的内容 ${relatedId}`
          })
        }
      }
    }
  }

  for (const asset of data.assets) {
    if (
      asset.displayRole !== 'cabinet-layout' ||
      asset.layoutTemplateId === null
    ) {
      continue
    }
    const cabinetProfile = cabinetProfiles.find((profile) =>
      asset.layoutTemplateId?.startsWith(`${profile.id}-`)
    )
    const cabinetEntity =
      cabinetProfile === undefined
        ? undefined
        : data.entities.find((entity) => entity.id === cabinetProfile.id)
    if (
      cabinetEntity === undefined ||
      !cabinetEntity.mediaIds.includes(asset.id)
    ) {
      issues.push({
        path: `assets.${asset.id}.layoutTemplateId`,
        message: '机柜布局素材必须由对应柜型实体引用，才能进入渠道投影'
      })
    }
  }

  if (isVersionAtLeast(data.minAppVersion, V020_MIN_APP_VERSION)) {
    for (const profile of cabinetProfiles) {
      const entity = data.entities.find((item) => item.id === profile.id)
      if (
        entity?.contentType !== 'product' ||
        entity.productCategory !== 'cabinet'
      ) {
        issues.push({
          path: `entities.${profile.id}`,
          message: 'v0.2.0 机柜生成器缺少对应的机柜产品实体'
        })
      }
    }
    for (const system of cabinetSystems) {
      const entity = data.entities.find(
        (item) => item.id === system.contentEntityId
      )
      if (
        entity?.contentType !== 'product' ||
        entity.productCategory !== 'ddc-controller'
      ) {
        issues.push({
          path: `entities.${system.contentEntityId}`,
          message: 'v0.2.0 机柜生成器缺少对应的控制系统实体'
        })
      }
    }
  }

  if (
    isVersionAtLeast(data.minAppVersion, V010_MIN_APP_VERSION) &&
    data.channel === 'windows_demo'
  ) {
    const softwareStyles = data.entities.filter(
      (entity) => entity.contentType === 'software-style'
    )
    const stylePackages = softwareStyles.filter(
      (entity) => entity.presentationMode === 'style-package'
    )

    if (softwareStyles.length !== 8 || stylePackages.length !== 8) {
      issues.push({
        path: 'entities.software-style',
        message:
          'v0.1.0 及以上 Windows 内容必须包含恰好 8 套完整软件风格包'
      })
    }

    for (const duplicate of findDuplicates(
      softwareStyles.map((entity) => entity.styleCode)
    )) {
      issues.push({
        path: `entities.software-style.${duplicate}.styleCode`,
        message: '软件风格包的 styleCode 必须唯一'
      })
    }

    const requiredPageKeys = new Set<string>(softwareStylePageKeys)
    for (const style of softwareStyles) {
      const pageKeys = style.pagePreviews.map((preview) => preview.pageKey)
      const previewMediaIds = style.pagePreviews.map(
        (preview) => preview.mediaId
      )
      const pageKeySet = new Set(pageKeys)
      const hasCompleteCoverage =
        pageKeys.length === softwareStylePageKeys.length &&
        pageKeySet.size === softwareStylePageKeys.length &&
        softwareStylePageKeys.every((pageKey) => pageKeySet.has(pageKey))

      if (!hasCompleteCoverage) {
        issues.push({
          path: `entities.${style.id}.pagePreviews`,
          message: '每套软件风格必须完整覆盖 10 个标准功能模块，且 pageKey 不得重复'
        })
      }

      if (new Set(previewMediaIds).size !== softwareStylePageKeys.length) {
        issues.push({
          path: `entities.${style.id}.pagePreviews`,
          message: '每套软件风格的 10 个功能模块必须分别引用唯一的页面预览媒体'
        })
      }

      for (const pageKey of pageKeySet) {
        if (!requiredPageKeys.has(pageKey)) {
          issues.push({
            path: `entities.${style.id}.pagePreviews`,
            message: `引用了非标准功能模块 ${pageKey}`
          })
        }
      }

      const styleMediaIds = new Set(style.mediaIds)
      for (const preview of style.pagePreviews) {
        const hasCompleteCustomerIntroduction =
          preview.customerIntro.trim().length > 0 &&
          preview.customerValue.trim().length > 0 &&
          preview.capabilities.length >= 3 &&
          preview.capabilities.length <= 5 &&
          preview.capabilities.every(
            (capability) => capability.trim().length > 0
          )

        if (!hasCompleteCustomerIntroduction) {
          issues.push({
            path: `entities.${style.id}.pagePreviews.${preview.pageKey}`,
            message:
              '每个业务面必须包含客户介绍、3–5 个有效功能点和客户价值'
          })
        }

        if (!styleMediaIds.has(preview.mediaId)) {
          issues.push({
            path: `entities.${style.id}.pagePreviews.${preview.pageKey}.mediaId`,
            message: `页面预览媒体 ${preview.mediaId} 未登记在该风格包的 mediaIds 中`
          })
        }
      }
    }
  }

  return {
    success: issues.length === 0,
    data,
    issues
  }
}

function sanitizeEntityRelations(
  entity: ContentEntity,
  allowedEntityIds: Set<string>,
  allowedAssetIds: Set<string>
): ContentEntity {
  const base = {
    ...entity,
    mediaIds: entity.mediaIds.filter((id) => allowedAssetIds.has(id))
  }
  if (base.contentType === 'project-case') {
    return {
      ...base,
      relatedEntityIds: base.relatedEntityIds.filter((id) => allowedEntityIds.has(id))
    }
  }
  return base
}

export function projectContentForChannel(
  manifest: ContentManifest,
  channel: DeliveryChannel,
  now = new Date()
): ContentManifest {
  const entities = manifest.entities.filter((entity) =>
    canUseOnChannel(entity, channel, now)
  )
  const referencedAssetIds = new Set(entities.flatMap((entity) => entity.mediaIds))
  const assets = manifest.assets.filter(
    (asset) =>
      referencedAssetIds.has(asset.id) && canUseOnChannel(asset, channel, now)
  )
  const allowedEntityIds = new Set(entities.map((entity) => entity.id))
  const allowedAssetIds = new Set(assets.map((asset) => asset.id))

  return {
    ...manifest,
    channel,
    entities: entities.map((entity) =>
      sanitizeEntityRelations(entity, allowedEntityIds, allowedAssetIds)
    ),
    assets
  }
}

export function findEntity(
  manifest: ContentManifest,
  entityId: string
): ContentEntity | undefined {
  return manifest.entities.find((entity) => entity.id === entityId)
}

export function findAsset(
  manifest: ContentManifest,
  assetId: string
): MediaAsset | undefined {
  return manifest.assets.find((asset) => asset.id === assetId)
}
